import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listMerchants, type Merchant } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Plus, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { MerchantCreateModal } from "@/components/MerchantCreateModal";

export function MerchantsListPage() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listMerchants({ limit: 100 });
      setMerchants(resp.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const columns: ColumnDef<Merchant>[] = [
    {
      key: "business_name",
      header: "Marchand",
      minWidth: 280,
      sortable: true,
      accessor: (r) => r.business_name,
      cell: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 font-bold flex items-center justify-center text-sm shrink-0">
            {r.business_name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[#181c32] truncate">{r.business_name}</div>
            <div className="text-xs text-[#7e8299] font-mono truncate">{r.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: "business_email",
      header: "Email",
      minWidth: 200,
      sortable: true,
      accessor: (r) => r.business_email,
      cell: (r) => <span className="text-sm">{r.business_email}</span>,
    },
    {
      key: "country",
      header: "Pays",
      minWidth: 80,
      sortable: true,
      accessor: (r) => r.country,
      cell: (r) => <span>{r.country}</span>,
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 100,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "livemode",
      header: "Mode",
      minWidth: 90,
      cell: (r) => (
        <Badge color={r.livemode ? "info" : "light"}>{r.livemode ? "Live" : "Test"}</Badge>
      ),
    },
    {
      key: "created_at",
      header: "Créé le",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.created_at,
      cell: (r) => <span className="text-sm text-[#7e8299]">{formatDate(r.created_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Marchands aviation"
        breadcrumbs={[{ label: "Administration" }, { label: "Marchands" }]}
        actions={
          <>
            <button onClick={load} className="btn btn-light btn-sm" title="Rafraîchir">
              <RefreshCw size={14} />
            </button>
            <button onClick={() => setCreateOpen(true)} className="btn btn-primary btn-sm">
              <Plus size={16} />
              Nouveau marchand
            </button>
          </>
        }
      />

      <MerchantCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => load()}
      />

      <DataTable<Merchant>
        title="Compagnies aériennes intégrées"
        subtitle={`${merchants.length} marchand${merchants.length > 1 ? "s" : ""}`}
        searchable
        searchPlaceholder="Rechercher par nom, email…"
        columns={columns}
        data={merchants}
        rowKey={(r) => r.id}
        loading={loading}
        onRowClick={(r) => navigate(`/admin/merchants/${r.id}`)}
        emptyMessage="Aucun marchand. Cliquez sur Nouveau marchand pour commencer."
      />
    </div>
  );
}
